<template>
  <div
    class="confidence-pills"
    :class="{ loading: effectiveLoading }"
    role="group"
    aria-label="Confidence Filters"
  >
    <button
      v-for="level in confidence_levels"
      :key="level.key"
      type="button"
      class="confidence-pill"
      :class="{ active: isActive(level.key) }"
      :disabled="effectiveLoading || !interactive"
      :aria-pressed="isActive(level.key)"
      :aria-label="getAriaLabel(level)"
      @click="toggle(level)"
    >
      <span
        class="value"
        :class="{'show-counts': showCounts}"
      >
        {{ level.name }} <span>({{ level.count }})</span>
      </span>
      <i
        v-if="isActive(level.key)"
        class="hi hi-check"
        aria-hidden="true"
      />
    </button>
  </div>
</template>

<script>
import { INSIGHTS_CONFIDENCE_LEVELS } from '~/app_constants';
import { mapState, mapGetters } from 'vuex';
import { debounce } from 'lodash-es';
import { getInsightsPreference, setInsightsPreference } from '~/utils/insights_utils';

const DEFAULT_ACTIVE = ['high', 'medium'];

/**
 * InsightsUnusualActivityConfidencePills
 *
 * Renders filter pills for unusual activity confidence levels.
 * Supports multi-select, accessibility, loading state, and filter persistence.
 *
 * @emits 'click' - emits array of active keys whenever selection changes
 * @prop {Object} confidenceCounts - object: confidence key => count
 * @prop {Boolean} isLoading - disables UI and prevents interaction when true
 */
export default {
  name: 'InsightsUnusualActivityConfidencePills',
  props: {
    confidenceCounts: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    showCounts: {
      type: Boolean,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    },
    interactive: {
      type: Boolean,
      default: true,
    }
  },
  emits: ['click'],
  data() {
    return {
      // Tracks active (selected) confidence level keys
      activeConfidenceLevels: [],
      // Only keep the loading state for the very first load
      firstLoadActive: true,
    };
  },
  computed: {
    ...mapState(['filters']),
    ...mapGetters(['organizationId']),
    // Use only during the very first load
    effectiveLoading() {
      return this.firstLoadActive && this.isLoading;
    },
    /**
     * Builds list of confidence levels with their names and current counts.
     * Clones and reverses the constant array to avoid mutating imported data.
     */
    confidence_levels() {
      return [...INSIGHTS_CONFIDENCE_LEVELS]
        .map(({ key, name }) => ({
          key,
          name,
          count: this.confidenceCounts[key] || 0
        }))
        .reverse();
    },
  },
  watch: {
    // Turn off first-load once the initial loading completes
    isLoading(newVal) {
      if (newVal === false && this.firstLoadActive) {
        this.firstLoadActive = false;
      }
    },
  },
  created() {
    const storeConfidenceLevels = this.filters.confidence_levels;
    const stored = getInsightsPreference(this.organizationId, 'ua_confidence_levels');
    const currentLevels = storeConfidenceLevels ? [...storeConfidenceLevels]
      : (Array.isArray(stored) ? stored : [...DEFAULT_ACTIVE]);

    this.activeConfidenceLevels = this.interactive ? currentLevels : [];

    if (this.interactive) {
      this.$emit('click', currentLevels);
    }

    // Debounced emitter for rapid toggles
    this.emitDebounced = debounce((levels) => {
      this.$emit('click', levels);
    }, 300);
  },
  beforeDestroy() {
    if (this.emitDebounced?.cancel) {
      this.emitDebounced.cancel();
    }
  },
  methods: {
    /**
     * Toggles (adds or removes) the given level from the active list,
     * saves the new list to localStorage, and emits the update event.
     * @param {object} level
     */
    toggle(level) {
      if (!this.interactive || this.effectiveLoading) {
        return;
      }

      const key = level.key;
      const newLevels = this.isActive(key)
        ? this.activeConfidenceLevels.filter(item => item !== key)
        : [...this.activeConfidenceLevels, key];

      this.activeConfidenceLevels = newLevels;
      setInsightsPreference(this.organizationId, 'ua_confidence_levels', newLevels);
      // Debounce outward emission to avoid spamming API on rapid clicks
      this.emitDebounced(newLevels);
      this.trackPillClick(level);
    },

    /**
     * Returns true if the given key is active (selected)
     * @param {string} key
     * @returns {boolean}
     */
    isActive(key) {
      return this.activeConfidenceLevels.includes(key);
    },

    /**
     * Builds a meaningful aria-label for accessibility for each pill button
     * Adds context for screen readers: disabled, active
     * @param {Object} level
     * @returns {string}
     */
    getAriaLabel(level) {
      const base = `${level.name} (${level.count})`;

      if (level.count === 0) {return `${base}, unavailable`;}
      if (this.isActive(level.key)) {return `${base}, selected`;}

      return base;
    },

    /**
     * Use analytics API to track clicks
     * @param {Object} level
     * @returns {null}
     */
    trackPillClick(level) {
      window.analytics?.track('Pill click', {
        pill_level_name: level.name,
        pill_instances_count: level.count,
        pill_active: this.isActive(level.key),
      });
    },
  }
};
</script>
